import React, { useState, useEffect, useRef } from 'react';
import useGameBase from '../../hooks/useGameBase';

function OneWordStoryGame({ gameState, gamePlayers, user, socket, roomId }) {
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = normalizedState || gameState || {};
  const [word, setWord] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  
  const socketId = socket ? socket.id : null;
  const suggestionsMap = state ? state.suggestions : null;

  useEffect(() => {
    // Update suggestions for this player from authoritative state
    try {
      // Only overwrite local suggestions when the server state explicitly contains
      // suggestions for this socketId. Do not clear local (ACK) suggestions when
      // the server state doesn't include them — ACKs should be visible even if
      // the server broadcasts state without per-player suggestions.
      if (suggestionsMap && socketId && Object.prototype.hasOwnProperty.call(suggestionsMap, socketId)) {
        setSuggestions(suggestionsMap[socketId] || []);
      }
    } catch (e) { setSuggestions([]); }
  }, [suggestionsMap, socketId]);

  const isMyTurn = state.currentPlayerSocketId && socket && socket.id && state.currentPlayerSocketId === socket.id;

  // Focus and select the input when it becomes your turn
  useEffect(() => {
    try {
      if (isMyTurn && !state.finished && inputRef && inputRef.current) {
        inputRef.current.focus();
        // select current contents so typing replaces it
        try { inputRef.current.select(); } catch (e) { /* ignore */ }
      }
    } catch (e) {}
  }, [isMyTurn, state.finished]);

  const doAddWord = async (w) => {
    if (!w || typeof w !== 'string') return;
    const clean = String(w).trim().split(/\s+/)[0];
    if (!clean) return;
    try {
      await submitAction({ type: 'addWord', word: clean });
      setWord('');
      setSuggestions([]);
      setError('');
    } catch (e) {
      setError('Failed to add word');
    }
  };

  const requestSuggestions = async () => {
    try {
      const res = await submitAction({ type: 'getSuggestions' });
      // Server returns suggestions in the ACK when LLM is used (or as fallback)
      if (res && Array.isArray(res.suggestions)) {
        setSuggestions(res.suggestions);
      } else if (res && res.status === 'ok' && Array.isArray(res.suggestions)) {
        setSuggestions(res.suggestions);
      } else {
        // If no suggestions returned, clear local suggestions (server may broadcast into state.suggestions)
        setSuggestions([]);
      }
    } catch (e) {
      // ignore but surface a small error
      setError('Failed to fetch suggestions');
    }
  };

  return (
    <div className="w-full p-4 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="mb-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">Players:</div>
        <div className="flex gap-2 mt-1">
          {(state.players || []).map(p => (
            <div key={p.socketId} className={`px-2 py-1 rounded text-sm ${state.currentPlayerSocketId === p.socketId ? 'bg-blue-200 dark:bg-blue-700 font-semibold' : 'bg-gray-100 dark:bg-gray-800'}`}>
              {p.username || p.userId || p.socketId}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-4">
        <div className="font-semibold mb-2">Story so far:</div>
        <div className="p-3 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 min-h-[4rem] text-gray-800 dark:text-gray-100">
          {(state.story || []).length === 0 ? <em className="text-gray-500">No words yet</em> : (
            <div className="whitespace-normal break-words">{(state.story || []).join(' ')}</div>
          )}
        </div>
      </div>

      <div className="mb-3">
        <div className="text-sm mb-1">Add a single word:</div>
        <div className="flex gap-2 items-center">
          <input
            ref={inputRef}
            type="text"
            value={word}
            onChange={e => setWord(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && isMyTurn && !state.finished) {
                e.preventDefault();
                doAddWord(word);
              }
            }}
            disabled={!isMyTurn || state.finished}
            placeholder={isMyTurn ? 'Enter a word' : 'Waiting for your turn...'}
            className={`px-2 py-1 rounded w-full bg-white dark:bg-gray-800 text-sm ${isMyTurn && !state.finished ? 'ring-2 ring-blue-300 border-blue-500' : 'border border-gray-300 dark:border-gray-600'}`}
          />
          <button
            onClick={() => doAddWord(word)}
            disabled={!isMyTurn || state.finished}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm"
          >Add</button>
          <button onClick={requestSuggestions} disabled={!isMyTurn || state.finished} className="px-3 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm">Suggest</button>
        </div>
        {error && <div className="text-red-600 mt-1">{error}</div>}
      </div>

      <div>
        <div className="text-sm mb-2">Suggestions:</div>
        <div className="flex flex-wrap gap-2">
          {suggestions && suggestions.length > 0 ? suggestions.map((s, i) => (
            <button key={i} onClick={() => doAddWord(s)} className="px-3 py-1 bg-gray-100 dark:bg-gray-800 rounded text-sm">{s}</button>
          )) : <div className="text-sm text-gray-500">No suggestions yet</div>}
        </div>
      </div>
    </div>
  );
}

OneWordStoryGame.Guide = function OneWordStoryGuide() {
  return (
    <div className="p-2 text-sm text-gray-800 dark:text-gray-200">
      Take turns adding one word to build a shared story. On your turn either type a single word or use the Suggest button to get up to 10 suggestions. There is no scoring — enjoy the story!
    </div>
  );
};

export default OneWordStoryGame;
